import React from 'react';
import { defineMessages, IntlShape, useIntl } from 'react-intl';
import { Icon } from '@plone/volto/components';
import calendarSVG from '@plone/volto/icons/calendar.svg';
import clockSVG from '@plone/volto/icons/clock.svg';
import CustomObjectListWidget from './CustomObjectListWidget';

import { formatDateLabel, formatDay, formatTimeRangeLabel } from 'volto-apanymantel/helpers';

const messages = defineMessages({
  addTimetable: {
    id: 'Add Timetable',
    defaultMessage: 'Add Timetable',
  },
  anyDay: {
    id: 'Any Day',
    defaultMessage: 'Any Day',
  },
  day: {
    id: 'Day',
    defaultMessage: 'Day',
  },
  fromDate: {
    id: 'From Date',
    defaultMessage: 'From Date',
  },
  fromTime: {
    id: 'From Time',
    defaultMessage: 'From Time',
  },
  repeatEveryYear: {
    id: 'Repeat every Year',
    defaultMessage: 'Repeat every Year',
  },
  toDate: {
    id: 'To Date',
    defaultMessage: 'To Date',
  },
  toTime: {
    id: 'To Time',
    defaultMessage: 'To Time',
  },
  withoutDay: {
    id: 'Without Day',
    defaultMessage: 'Without Day',
  },
});

const timetableSchema = (intl: IntlShape) => ({
  title: 'Date Ranges',
  addMessage: intl.formatMessage(messages.addTimetable),
  properties: {
    day: {
      title: intl.formatMessage(messages.day),
      type: 'integer',
      default: null,
      widget: 'radio_group',
      choices: [
        {0: null, 1: intl.formatMessage(messages.withoutDay)},
        {0: 0, 1: formatDay(0, false, intl)},
        {0: 1, 1: formatDay(1, false, intl)},
        {0: 2, 1: formatDay(2, false, intl)},
        {0: 3, 1: formatDay(3, false, intl)},
        {0: 4, 1: formatDay(4, false, intl)},
        {0: 5, 1: formatDay(5, false, intl)},
        {0: 6, 1: formatDay(6, false, intl)},
      ],
      noValueOption: true,
    },
    fromDate: {
      title: intl.formatMessage(messages.fromDate),
      type: 'date',
      default: null,
      widget: 'date',
    },
    toDate: {
      title: intl.formatMessage(messages.toDate),
      type: 'date',
      default: null,
      widget: 'date',
    },
    repeatEveryYear: {
      title: intl.formatMessage(messages.repeatEveryYear),
      type: 'boolean',
      default: false,
    },
    fromTime: {
      title: intl.formatMessage(messages.fromTime),
      type: 'time',
      widget: 'time',
    },
    toTime: {
      title: intl.formatMessage(messages.toTime),
      type: 'time',
      widget: 'time',
    },
  },
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['fromDate', 'toDate', 'repeatEveryYear', 'day', 'fromTime', 'toTime'],
    },
  ],
  required: ['fromTime', 'toTime'],
});

const TimetableWidget = (props): JSX.Element => {
  const intl = useIntl();

  const getItemTitle = React.useCallback(
    ({ item, index, fallback }) => {
      const hideYear = item?.repeatEveryYear === true;
      const fromDateLabel = formatDateLabel(item?.fromDate, intl, { hideYear });
      const toDateLabel = formatDateLabel(item?.toDate, intl, { hideYear });
      const timeRangeLabel = formatTimeRangeLabel(item.fromTime, item.toTime, intl);
      const dayLabel = formatDay(item.day, true, intl);

      const baseTitle = (() => {
        if (fromDateLabel && toDateLabel) {
          return (
            <span className="item-title" style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name={calendarSVG} size="24px" />
              <span style={{ marginLeft: '8px' }}>
                {fromDateLabel} - {toDateLabel}
                {dayLabel && <> ({dayLabel})</>}
              </span>
            </span>
          );
        }

        if (fromDateLabel || toDateLabel) {
          return (
            <span className="item-title" style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name={calendarSVG} size="24px" />
              <span style={{ marginLeft: '8px' }}>
                {fromDateLabel || toDateLabel}
                {dayLabel && <> ({dayLabel})</>}
              </span>
            </span>
          );
        }

        if (dayLabel) {
          return (
            <span className="item-title" style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name={calendarSVG} size="24px" />
              <span style={{ marginLeft: '8px' }}>{dayLabel}</span>
            </span>
          );
        }

        return (
            <span className="item-title" style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name={calendarSVG} size="24px" />
              <span style={{ marginLeft: '8px' }}>{intl.formatMessage(messages.anyDay)}</span>
            </span>
          );
        // return (
        //   fallback ||
        //   `${intl.formatMessage(messages.addTimetable)} #${index + 1}`
        // );
      })();

      const timeTitle = timeRangeLabel ? (
        <span className="item-title" style={{ display: 'flex', alignItems: 'center' }}>
          <Icon name={clockSVG} size="24px" />
          <span style={{ marginLeft: '8px' }}>{timeRangeLabel}</span>
        </span>
      ) : null;

      return (
        <div style={{ display: 'flex', gap: '24px' }}>
          <div style={{ minWidth: '200px', textAlign: 'center' }}>{baseTitle}</div>
          <div style={{ textAlign: 'center', minWidth: '100px' }}>{timeTitle}</div>
        </div>
      );
    },
    [intl],
  );

  return (
    <CustomObjectListWidget
      schema={timetableSchema(intl)}
      getItemTitle={getItemTitle}
      {...props}
      activeObject={-1}
      value={props.value?.items || props.default?.items || []}
      onChange={(id, value) => props.onChange(id, { items: value })}
    />
  );
};

export default TimetableWidget;
